import Image from "next/image";
import { Phone } from "lucide-react";

interface ExpertQuoteProps {
  quote: string;
  author: string;
  role: string;
  imagePath?: string;
  ctaText?: string;
  ctaHref?: string;
  authorHref?: string;
  authorLinkedIn?: string;
  /** Display form of the author's phone number, e.g. "+49 89 21542466".
   *  Rendered as a `tel:` link under the role so a reader can call at once. */
  phone?: string;
}

export default function ExpertQuote({
  quote,
  author,
  role,
  imagePath,
  ctaText,
  ctaHref,
  authorHref,
  authorLinkedIn,
  phone,
}: ExpertQuoteProps) {
  if (!quote || !author) {
    throw new Error("ExpertQuote: `quote` and `author` are required.");
  }

  const resolvedImage = imagePath
    ? imagePath.replace(/^@assets\//, "/assets/")
    : undefined;
  const phoneHref = phone ? `tel:${phone.replace(/[^+\d]/g, "")}` : undefined;

  return (
    <div className="expert-quote">
      <div className="expert-quote-grid">
        {resolvedImage ? (
          <div className="expert-quote-photo">
            <Image
              src={resolvedImage}
              alt={`${author}, ${role}`}
              width={480}
              height={480}
              sizes="(min-width: 980px) 240px, 50vw"
            />
          </div>
        ) : null}
        <blockquote className="expert-quote-text">
          <p>&ldquo;{quote}&rdquo;</p>
          <footer>
            <strong>
              {authorHref ? (
                <a href={authorHref}>{author}</a>
              ) : (
                author
              )}
              {authorLinkedIn ? (
                <a
                  href={authorLinkedIn}
                  target="_blank"
                  rel="noopener noreferrer me"
                  aria-label="LinkedIn-Profil"
                  className="expert-quote-linkedin"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                    width="16"
                    height="16"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              ) : null}
            </strong>
            <span>{role}</span>
            {phone && phoneHref ? (
              <a className="expert-quote-phone" href={phoneHref}>
                <Phone size={16} strokeWidth={2} aria-hidden="true" />
                {phone}
              </a>
            ) : null}
          </footer>
          {ctaText && ctaHref ? (
            <a className="btn btn-primary" href={ctaHref}>
              {ctaText}
            </a>
          ) : null}
        </blockquote>
      </div>
    </div>
  );
}
